<template>
    <div>
        <!-- Mobile -->
        <div v-if="$q.platform.is.mobile || $q.screen.lt.sm">
            <div v-for="(level, index) in levels" :key="index" class="q-pb-md">
                <div class="row items-start q-px-md">
                    <!-- lower value -->
                    <q-input
                        :model-value="level.lower"
                        type="number"
                        label="Lower"
                        :disable="index > 0"
                        class="col q-mr-sm"
                        color="accent"
                        @update:model-value="
                            level => {
                                level.lower[index] = parseInt(level);
                            }
                        "
                        outlined
                        suffix="%"
                        :rules="[
                            val =>
                                val > -1 ||
                                $t('Lower value should be greater than 0'),
                            val => val + 1 || $t('Lower value is required'),
                            val =>
                                val < parseInt(level.upper) ||
                                $t(
                                    'Lower value should be less than upper value'
                                ),
                            val =>
                                val == 0 ||
                                $t('Lowest value should start from 0')
                        ]"
                    />
                    <!-- upper value -->
                    <q-input
                        :model-value="level.upper"
                        type="number"
                        label="Upper"
                        class="col"
                        outlined
                        suffix="%"
                        color="accent"
                        @update:model-value="
                            level => {
                                handleUpperInput(level, index);
                            }
                        "
                        :rules="[
                            val => !!val || $t('Upper value is required'),
                            val =>
                                val > parseInt(level.lower) ||
                                $t(
                                    'Upper value should be greater than lower value'
                                ),
                            val =>
                                val <= 100 ||
                                $t('Upper value should be less than 100')
                        ]"
                    />
                </div>
                <div class="row items-start q-px-md">
                    <!-- color value -->
                    <q-input
                        :input-style="{
                            backgroundColor: level.color
                        }"
                        class="col q-mr-lg"
                        outlined
                        readonly
                        color="accent"
                    >
                        <template v-slot:append>
                            <q-icon name="colorize" class="cursor-pointer">
                                <q-popup-proxy
                                    transition-show="scale"
                                    transition-hide="scale"
                                >
                                    <q-color
                                        :model-value="level.color"
                                        default-view="palette"
                                        class="my-picker"
                                        no-footer
                                        no-header
                                        :palette="paletteOptions"
                                        @update:model-value="
                                            color => {
                                                handleColorInput(color, index);
                                            }
                                        "
                                    />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>

                    <!-- add new level -->
                    <q-btn
                        id="add-new-level-btn"
                        class="q-my-sm"
                        v-if="index == levels.length - 1"
                        @click="addNewLevel"
                        icon="fas fa-plus"
                        color="accent"
                        outline
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Add new level') }}
                        </q-tooltip>
                    </q-btn>

                    <!-- delete level -->

                    <q-btn
                        v-else
                        @click="deleteLevel(index)"
                        class="q-my-sm"
                        icon="fas fa-trash"
                        color="accent"
                        outline
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Delete level') }}
                        </q-tooltip>
                    </q-btn>
                </div>
            </div>
        </div>
        <!-- Desktop view -->
        <div v-else>
            <div
                v-for="(level, index) in levels"
                :key="index"
                class="row items-start q-px-md"
            >
                <!-- lower value -->
                <q-input
                    :model-value="level.lower"
                    :data-test="`lower-input-${index}`"
                    type="number"
                    label="Lower"
                    :disable="index > 0"
                    class="col q-mr-lg"
                    color="accent"
                    @update:model-value="
                        val => {
                            levels[index].lower = parseInt(val);
                        }
                    "
                    outlined
                    suffix="%"
                    :rules="[
                        val =>
                            val > -1 ||
                            $t('Lower value should be greater than 0'),
                        val => val + 1 || $t('Lower value is required'),
                        val =>
                            val < parseInt(level.upper) ||
                            $t('Lower value should be less than upper value'),
                        val =>
                            index !== 0 ||
                            val == 0 ||
                            $t('Lowest value should start from 0')
                    ]"
                />
                <!-- upper value -->
                <q-input
                    :model-value="level.upper"
                    :data-test="`upper-input-${index}`"
                    type="number"
                    label="Upper"
                    class="col q-mr-lg"
                    outlined
                    suffix="%"
                    color="accent"
                    @update:model-value="
                        level => {
                            handleUpperInput(level, index);
                        }
                    "
                    :rules="[
                        val => !!val || $t('Upper value is required'),
                        val =>
                            val > parseInt(level.lower) ||
                            $t(
                                'Upper value should be greater than lower value'
                            ),
                        val =>
                            val <= 100 ||
                            $t('Upper value should be less than 100')
                    ]"
                />

                <!-- color value -->
                <q-input
                    :input-style="{
                        backgroundColor: level.color
                    }"
                    class="col q-mr-lg"
                    outlined
                    readonly
                    color="accent"
                >
                    <template v-slot:append>
                        <q-icon
                            name="colorize"
                            class="cursor-pointer"
                            color="accent"
                        >
                            <q-popup-proxy
                                transition-show="scale"
                                transition-hide="scale"
                            >
                                <q-color
                                    :model-value="level.color"
                                    default-view="palette"
                                    class="my-picker"
                                    no-footer
                                    no-header
                                    :palette="paletteOptions"
                                    @update:model-value="
                                        color => {
                                            handleColorInput(color, index);
                                        }
                                    "
                                />
                            </q-popup-proxy>
                        </q-icon>
                    </template>
                </q-input>

                <!-- add new level -->
                <q-btn
                    id="add-new-level-btn"
                    class="q-my-sm"
                    v-if="index == levels.length - 1"
                    @click="addNewLevel"
                    icon="fas fa-plus"
                    color="accent"
                    outline
                >
                    <q-tooltip style="font-size: 14px">
                        {{ $t('Add new level') }}
                    </q-tooltip>
                </q-btn>

                <!-- delete level -->

                <q-btn
                    v-else
                    @click="deleteLevel(index)"
                    class="q-my-sm"
                    icon="fas fa-trash"
                    color="accent"
                    outline
                    :data-test="`delete-level-${index}`"
                >
                    <q-tooltip style="font-size: 14px">
                        {{ $t('Delete level') }}
                    </q-tooltip>
                </q-btn>
            </div>
        </div>
    </div>
</template>

<script>
// Color Picker default color
let defaultColor = '#999999';
export default {
    name: 'SpeedColorLevel',
    emits: ['input'],
    props: {
        modelValue: {
            type: Array,
            required: true
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                this.levels = JSON.parse(JSON.stringify(value));
            }
        }
    },
    data() {
        return {
            levels: [],
            paletteOptions: [
                '#f50000',
                '#f57b00',
                '#f5f500',
                '#00f500',
                '#00f57b',
                '#00f5f5',
                '#007bf5',
                '#0000f5',
                '#7b00f5',
                '#f500f5',
                '#d60000',
                '#d66c00',
                '#d6d600',
                '#00d600',
                '#00d66c',
                '#00d6d6',
                '#006cd6',
                '#0000d6',
                '#6c00d6',
                '#d600d6',
                '#a30000',
                '#a35200',
                '#a3a300',
                '#00a300',
                '#00a352',
                '#00a3a3',
                '#0052a3',
                '#0000a3',
                '#5200a3',
                '#a300a3',
                '#5c0000',
                '#5c2e00',
                '#5c5c00',
                '#005c00',
                '#005c2e',
                '#005c5c',
                '#002e5c',
                '#00005c',
                '#2e005c',
                '#5c005c'
            ]
        };
    },

    methods: {
        /**
         * Trigger when the user add new level
         * */
        addNewLevel() {
            // empty level
            let newLevel = {
                color: defaultColor,
                lower: 0,
                upper: 100
            };
            // sync the levels
            newLevel['lower'] = this.levels[this.levels.length - 1]['upper'];
            this.levels.push(newLevel);
            this.levelChanged();
        },
        /**
         * Trigger when the user deletes the level
         * @param {Number} index
         * */
        deleteLevel(index) {
            if (this.levels.length > 1) {
                if (index === 0) {
                    this.levels[index + 1].lower = this.levels[index].lower;
                } else if (index === this.levels.length - 1) {
                    this.levels[index - 1].upper = this.levels[index].upper;
                } else {
                    this.levels[index + 1].lower = this.levels[index].lower;
                }
                this.levels.splice(index, 1);
            }
            this.levelChanged();
        },
        /**
         * Trigger when the user changes the upper value of an existing level
         * @param {Number} newUpperValue
         * @param {Number} rangeIndex
         * */
        handleUpperInput: function (newUpperValue, rangeIndex) {
            this.levels[rangeIndex].upper = parseInt(newUpperValue);
            // Sync the lower value of the next range with this updated upper value of this range
            if (rangeIndex + 1 < this.levels.length) {
                this.levels[rangeIndex + 1].lower = parseInt(newUpperValue);
            }
            this.levelChanged();
        },
        /**
         * Trigger when the user changes the color of an existing level
         * @param {String} newColor
         * @param {Number} rangeIndex
         */
        handleColorInput: function (newColor, rangeIndex) {
            this.levels[rangeIndex].color = newColor;
            this.levelChanged();
        },
        /**
         * Emit the input event
         * We could use a watcher to emit the input event but it will be triggered multiple times
         */
        levelChanged() {
            this.$emit('input', this.levels);
        }
    }
};
</script>

<style lang="scss" scoped>
.my-picker {
    max-width: 300px;
}
</style>
